/**
 * The hosts this page's own Content-Security-Policy has refused a call to.
 *
 * Since the policy is enforced (D336), a `connect-src` that does not list a host
 * refuses the request before it leaves the page, and `fetch` rejects with the same
 * bare TypeError a dead network gives. The adapters then said "Could not reach
 * the API… check your connection", and a self-hoster who had pointed Your own
 * endpoint at Ollama or a proxy — which the shipped `_headers` does not list —
 * went to debug a firewall and CORS settings for a refusal the page made itself.
 * The browser does say which it was, on another channel: a
 * `securitypolicyviolation` event names the directive and the URL. Listening for
 * it once, here, is what lets the failure's sentence name the real cause.
 *
 * Hosts only, never a path or a query — what the policy refused is decided by
 * origin, and that is all a sentence needs. Nothing here decides anything: the
 * error's kind is `network` either way (invariant 7), and only its words change.
 * No document (the MCP server, the tests' Node) means nothing is ever refused.
 */

const refused = new Set<string>()

function onViolation(e: SecurityPolicyViolationEvent): void {
  const directive = e.effectiveDirective || e.violatedDirective || ''
  if (!directive.startsWith('connect-src')) return
  try {
    refused.add(new URL(e.blockedURI).host)
  } catch {
    // 'inline', 'eval' or an empty report: not a host, and not a connect.
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('securitypolicyviolation', onViolation)
}

/** Whether this page's policy has refused a call to `host` (`api.x.com`, `localhost:11434`). */
export function cspRefused(host: string): boolean {
  return refused.has(host)
}

/** The sentence for a call the policy refused: the cause, and the one fix. */
export function cspRefusedSentence(host: string): string {
  return `This site’s security policy (connect-src) does not allow calls to ${host}, so the browser refused the request before it left the page. Add that origin to connect-src in public/_headers and rebuild — see docs/self-hosting.md.`
}
